import { doc, getFirestore } from 'firebase/firestore';
import { computed } from 'mobx';
import {
  tenancyAgreementStatusTypes,
  tenancyDepositStatusTypes,
  TenancyStatusTypes,
} from 'config/types';
import { createObservableInstance } from '../../../helpers';
import FirebaseDoc from '../../abstract/FirebaseDoc';
import NoticesState from './NoticesState';
import PaymentsState from './PaymentsState';
import TenantsState from './TenantsState';

class TenancyState extends FirebaseDoc {
  constructor(root, id) {
    super(root, { ref: doc(getFirestore(), `tenancies/${id}`) });
  }

  get isFuture() {
    return (
      this.isInitialized &&
      this.isActive &&
      this.data?.dates?.startDate.toDate() > new Date()
    );
  }

  get isPast() {
    return (
      this.isInitialized &&
      this.isActive &&
      this.data?.dates?.endDate.toDate() < new Date()
    );
  }

  get notTerminated() {
    return this.data?.status !== TenancyStatusTypes.terminated;
  }

  get isCurrent() {
    return (
      this.isInitialized &&
      this.isActive &&
      this.data?.dates?.startDate.toDate() < new Date()
    );
  }

  get isActive() {
    return this.isInitialized && this.notTerminated;
  }

  get agreementSigned() {
    return (
      this.isInitialized &&
      this.data?.agreement?.status === tenancyAgreementStatusTypes.signed
    );
  }

  get tenantsDepositPaid() {
    return (
      this.isInitialized &&
      this.data &&
      this.data.payments.depositAmountPaid >= this.data.payments.depositAmount
    );
  }

  get depositStatus() {
    return (this.isInitialized && this.data && this.data.deposit.status) || '';
  }

  get depositRegistered() {
    return [
      tenancyDepositStatusTypes.protected,
      tenancyDepositStatusTypes.registeredNotPaid,
    ].includes(this.depositStatus);
  }

  get depositProtected() {
    return (
      this.isInitialized &&
      this.data?.deposit?.status === tenancyDepositStatusTypes.protected
    );
  }

  get tenantsLinked() {
    return (
      this.isInitialized &&
      this.data &&
      this.data.tenantUids.length >= this.data.tenantCount
    );
  }

  get tenantsStandingOrdersSetup() {
    return (
      this.isInitialized &&
      this.data &&
      this.data.payments.standingOrderAmount >= this.data.payments.rentAmount
    );
  }

  get propertyIsOccupied() {
    return this.isInitialized && this.data?.propertyIsOccupied;
  }

  init() {
    super.init();

    this.tenants = createObservableInstance(TenantsState, this);
    this.payments = createObservableInstance(PaymentsState, this);
    this.notices = createObservableInstance(NoticesState, this);
  }

  destroy() {
    super.destroy();

    this.tenants.destroy();
    this.payments.destroy();
    this.notices.destroy();
  }
}

TenancyState.annotations = {
  ...FirebaseDoc.annotations,
  isActive: computed,
  agreementSigned: computed,
  tenantsDepositPaid: computed,
  depositRegistered: computed,
  depositProtected: computed,
  tenantsLinked: computed,
  tenantsStandingOrdersSetup: computed,
  propertyIsOccupied: computed,
};

export default TenancyState;
